import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import {
    applyTheme,
    prefersLightQuery,
    readPreference,
    resolveTheme,
    writePreference,
    type Theme,
    type ThemePreference,
} from '../lib/theme';

interface ThemeContextType {
    /** What the user chose: dark, ivory, or follow the device. */
    preference: ThemePreference;
    /** What is on screen now. */
    theme: Theme;
    setPreference: (pref: ThemePreference) => void;
    /** Pin the whole document to one theme while a surface is mounted. */
    setForced: (theme: Theme | null) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * The theme was already applied before first paint by the inline script in
 * index.html, so this starts from the same answer and never flashes. It then
 * keeps <html data-theme> in step with the user's choice and — for "Match
 * device" — with the device's own appearance as it changes.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
    const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
    const [prefersLight, setPrefersLight] = useState(() => prefersLightQuery()?.matches ?? false);

    useEffect(() => {
        const mq = prefersLightQuery();
        if (!mq) return;
        const onChange = (e: MediaQueryListEvent) => setPrefersLight(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    // A surface that is only ever dark (the internal Tower, the blog admin)
    // pins the document while it is mounted — portalled modals included —
    // and the user's choice comes back when it unmounts.
    const [forced, setForced] = useState<Theme | null>(null);
    const theme = forced ?? resolveTheme(preference, prefersLight);

    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    const setPreference = useCallback((pref: ThemePreference) => {
        writePreference(pref);
        setPreferenceState(pref);
    }, []);

    return (
        <ThemeContext.Provider value={{ preference, theme, setPreference, setForced }}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}

/** Renders its children with the whole document pinned to `theme`. */
export function ForcedTheme({ theme, children }: { theme: Theme; children: React.ReactNode }) {
    const { setForced } = useTheme();
    useEffect(() => {
        setForced(theme);
        return () => setForced(null);
    }, [theme, setForced]);
    return <>{children}</>;
}
